import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Amplify } from 'aws-amplify';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';

// Configure Amplify with Cognito + (optionally) the AppSync GraphQL API used by the
// admin notification centre (Trello #192). The GraphQL block is only added when the
// AppSync endpoint env var is set, so a build without it (or before the API is
// applied) configures cleanly — the notification subscription simply stays inactive.
const appsyncEndpoint = import.meta.env.VITE_APPSYNC_API_URL || '';
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID || '',
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID || '',
    }
  },
  ...(appsyncEndpoint
    ? {
        API: {
          GraphQL: {
            endpoint: appsyncEndpoint,
            region: import.meta.env.VITE_APPSYNC_REGION || 'af-south-1',
            defaultAuthMode: 'userPool' as const,
          },
        },
      }
    : {}),
});

// Create admin dashboard theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1a365d',
      light: '#2c5282',
      dark: '#0f2340',
    },
    secondary: {
      main: '#38a169',
      light: '#48bb78',
      dark: '#276749',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    error: {
      main: '#e53e3e',
    },
    warning: {
      main: '#dd6b20',
    },
    info: {
      main: '#3182ce',
    },
    success: {
      main: '#38a169',
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>
);

